"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.sortAlertsBySelection = exports.attentionBadgeText = exports.alertAttentionMessage = exports.activeAlertsToStatus = exports.hazardAlerts = exports.activeAlerts = exports.alertCardSeverity = exports.isSensorOfflineAlert = exports.alertToCardStatus = exports.alertSeverityToStatus = exports.getAlertTitle = exports.alertVariant = void 0;
const types_1 = require("../types");
const sensorClassification_1 = require("./sensorClassification");
// HVAC_COMPRESSOR_FAULT carries its rule in metadata.variant; the title is per
// variant (the body text arrives in the alert's `message`).
const COMPRESSOR_FAULT_TITLES = {
    FAILED_START: 'Outdoor Unit Failed to Start',
    SHORT_CYCLING: 'Outdoor Unit Short Cycling',
    CLIMB_TRIP: 'Outdoor Unit Shutting Off Under Load',
    RUNNING_NO_SPLIT: 'System Running but Not Cooling or Heating',
};
/** `metadata.variant` of an alert (the schema's `metadata` is a JSON string). */
function alertVariant(alert) {
    if (typeof alert.metadata !== 'string')
        return undefined;
    try {
        const variant = JSON.parse(alert.metadata)?.variant;
        return typeof variant === 'string' ? variant : undefined;
    }
    catch {
        return undefined;
    }
}
exports.alertVariant = alertVariant;
/**
 * Human-readable title for an alert type. Lives here rather than in
 * `useActiveAlerts` because the aggregation hooks need it too: an alert that
 * drives a dashboard card's status has to be able to say WHICH alert it was
 * ("Upstairs: HVAC Current Sensor Fault"), and importing a hook module from a
 * hook module to get one pure string map is how import cycles start.
 */
function getAlertTitle(alertType, variant) {
    switch (alertType) {
        case types_1.SensorAlertType.Leak:
            return 'Leak Detected';
        case types_1.SensorAlertType.HighHumidity:
            return 'High Humidity';
        case types_1.SensorAlertType.LowHumidity:
            return 'Low Humidity';
        case types_1.SensorAlertType.HVACfilter:
        case types_1.SensorAlertType.HvacFilter:
            return 'Filter Change Overdue';
        case types_1.SensorAlertType.HighMCTCurrent:
        case types_1.SensorAlertType.HighBCTCurrent:
        case types_1.SensorAlertType.HighCurrent:
            return 'Power Surge Detected';
        case types_1.SensorAlertType.LowMCTCurrent:
        case types_1.SensorAlertType.LowBCTCurrent:
        case types_1.SensorAlertType.LowCurrent:
            return 'Low Power Detected';
        case types_1.SensorAlertType.HighVoltage:
            return 'High Voltage Detected';
        case types_1.SensorAlertType.LowVoltage:
            return 'Low Voltage Detected';
        case types_1.SensorAlertType.HighPressure:
            return 'High Pressure';
        case types_1.SensorAlertType.LowPressure:
            return 'Low Pressure';
        case types_1.SensorAlertType.HighIAQ:
            return 'Poor Air Quality';
        case types_1.SensorAlertType.HighPM:
            return 'High Particulate Matter';
        case types_1.SensorAlertType.HighVOC:
            return 'High VOC Levels';
        case types_1.SensorAlertType.HighCO2:
            return 'High CO₂ Levels';
        case types_1.SensorAlertType.HvacDegraded:
            return 'HVAC Running Inefficiently';
        case types_1.SensorAlertType.HvacCtFault:
            return 'HVAC Current Sensor Fault';
        case types_1.SensorAlertType.HvacCompressorFault:
            return (variant && COMPRESSOR_FAULT_TITLES[variant]) || 'Outdoor Unit Fault';
        case types_1.SensorAlertType.WqSensorFault:
            return 'Water Quality Sensor Fault';
        case types_1.SensorAlertType.HvacSensorFault:
            return 'HVAC Sensor Fault';
        case types_1.SensorAlertType.HighEC:
            return 'Poor Water Quality';
        case types_1.SensorAlertType.LowEC:
            return 'Low Water Conductivity';
        case types_1.SensorAlertType.HighTC:
            return 'High Temperature Alert';
        case types_1.SensorAlertType.LowTC:
            return 'Low Temperature Alert';
        case types_1.SensorAlertType.LowBattery:
            return 'Low Battery';
        case types_1.SensorAlertType.SensorInactive:
            return 'Sensor Not Reporting';
        case types_1.SensorAlertType.AqSensorInactive:
            return 'Air Quality Monitor Not Reporting';
        case types_1.SensorAlertType.HubInactive:
            return 'Base Station Offline';
        case types_1.SensorAlertType.HubOffline:
            return 'Base Station Lost Internet';
        case types_1.SensorAlertType.HubRadioFault:
            return 'Base Station Not Receiving Sensors';
        default:
            return 'Sensor Alert';
    }
}
exports.getAlertTitle = getAlertTitle;
/**
 * Severity → card status. Low/Info map to `Poor`, not `Good`: an active
 * unacknowledged alert is by definition something the homeowner still has to
 * deal with, and a green card with an open alert on it is the exact
 * misreporting this mapping exists to prevent.
 */
function alertSeverityToStatus(severity) {
    switch (severity) {
        case types_1.SensorAlertSeverity.Critical:
        case types_1.SensorAlertSeverity.High:
            return types_1.SensorCardStatus.Error;
        default:
            return types_1.SensorCardStatus.Poor;
    }
}
exports.alertSeverityToStatus = alertSeverityToStatus;
/**
 * Alert → card status, keyed on alert TYPE first and severity second.
 *
 * This exists because severity alone gets the coverage channel wrong. A sensor
 * that has stopped reporting carries its own SENSOR_INACTIVE row; at Medium
 * that row maps to `Poor` via the severity table above, and
 * `worstStatus(Offline, Poor)` is `Poor` — so the sensor's own offline alert
 * would drag its card back to amber-hazard, exactly the misreporting the
 * Offline channel exists to avoid. Dropping the sweep's severity to Medium is
 * NOT sufficient on its own; this function is the other half.
 *
 * Only SENSOR-level offline types are folded this way. Hub-level outages
 * (HUB_INACTIVE / HUB_OFFLINE / HUB_RADIO_FAULT) stay on the severity path and
 * therefore stay `Error`: a dead base station darkens every sensor at once,
 * which is a hazard, not a gap in one sensor's coverage.
 */
function alertToCardStatus(alert) {
    if (isSensorOfflineAlert(alert)) {
        return types_1.SensorCardStatus.Offline;
    }
    return alertSeverityToStatus(alert.severity);
}
exports.alertToCardStatus = alertToCardStatus;
/**
 * A SENSOR-level offline row (`SENSOR_INACTIVE` / `AQ_SENSOR_INACTIVE`),
 * whatever severity it was minted at. Hub-level outages are deliberately NOT
 * included — see `alertToCardStatus`.
 */
function isSensorOfflineAlert(alert) {
    // Loose shape on purpose: `AlertCardData` (what the detail screens hold)
    // carries `alertType` as a plain string, and they need this same predicate
    // so their "N Issues Detected" count agrees with the dashboard card's.
    return types_1.SENSOR_OFFLINE_ALERT_TYPES.includes(alert.alertType);
}
exports.isSensorOfflineAlert = isSensorOfflineAlert;
/**
 * Alert → alert-CARD colour channel (`ALERT_SEVERITY_COLORS`). Type first,
 * severity second, for the same reason as `alertToCardStatus`: a sensor's own
 * "not reporting" row is a coverage gap and renders coverage-yellow, whatever
 * severity it was minted at — Medium used to put it in hazard orange beside a
 * yellow coverage badge saying the same thing. Hub-level outages stay on the
 * severity path (High → `error`): a dead base station is red everywhere.
 */
function alertCardSeverity(alert) {
    if (isSensorOfflineAlert(alert))
        return 'coverage';
    switch (alert.severity) {
        case types_1.SensorAlertSeverity.Critical:
        case types_1.SensorAlertSeverity.High:
            return 'error';
        case types_1.SensorAlertSeverity.Medium:
            return 'warning';
        case types_1.SensorAlertSeverity.Low:
        case types_1.SensorAlertSeverity.Info:
            return 'info';
        default:
            return 'warning';
    }
}
exports.alertCardSeverity = alertCardSeverity;
/** Active (unacknowledged, unresolved) alerts out of a raw alert list. */
function activeAlerts(alerts) {
    return alerts.filter((a) => (0, types_1.isAlertActive)(a));
}
exports.activeAlerts = activeAlerts;
/**
 * Active alerts that belong in the HAZARD channel — the amber attention badge,
 * `activeAlertCount`, `needsAttentionCount`, and per-row `alertCount`.
 *
 * `alertToCardStatus` was only half the fix. It stopped an offline sensor's
 * own SENSOR_INACTIVE row from dragging the card to Poor, but the row was
 * still COUNTED as a hazard and its text still reached the attention badge —
 * so one dead sensor rendered twice on the same card: once in hazard amber
 * ("Water quality at AO Desk isn't being checked") and again in the coverage
 * badge ("Not monitoring — water quality at AO Desk isn't being checked").
 * Offline is reported through `coverage` and nowhere else. Feed the STATUS
 * channel `activeAlerts` (type-aware, folds to Offline); feed the hazard
 * channel this.
 *
 * A LOW_BATTERY sitting beside the offline row is kept — that one is a fact
 * the homeowner can act on, distinct from the gap itself.
 */
function hazardAlerts(alerts) {
    return alerts.filter((a) => (0, types_1.isAlertActive)(a) && !isSensorOfflineAlert(a));
}
exports.hazardAlerts = hazardAlerts;
/** Worst status implied by a set of alerts. Inactive alerts contribute nothing. */
function activeAlertsToStatus(alerts) {
    return alerts.reduce((worst, alert) => (0, types_1.isAlertActive)(alert) ? (0, sensorClassification_1.worstStatus)(worst, alertToCardStatus(alert)) : worst, types_1.SensorCardStatus.Good);
}
exports.activeAlertsToStatus = activeAlertsToStatus;
/**
 * Attention-badge text for one sensor's alerts — the specific fault when
 * there's one, a count when there are several.
 */
function alertAttentionMessage(label, alerts) {
    if (alerts.length === 0)
        return undefined;
    if (alerts.length === 1) {
        const [only] = alerts;
        return `${label}: ${getAlertTitle(only.alertType, alertVariant(only))}`;
    }
    return `${label}: ${alerts.length} Alerts`;
}
exports.alertAttentionMessage = alertAttentionMessage;
/**
 * Attention-badge text for a whole dashboard card. Prefers naming the actual
 * problem when there is exactly one; otherwise reports the alert total
 * alongside how many sensors it spans, so "3 Alerts · 1 of 2 Systems" reads
 * correctly when all three sit on one system.
 */
function attentionBadgeText(params) {
    const { activeAlertCount, needsAttentionCount, sensorCount, worstSensor, unitLabel = 'Sensor', } = params;
    if (activeAlertCount === 0 && needsAttentionCount === 0)
        return undefined;
    const units = `${unitLabel}${sensorCount === 1 ? '' : 's'}`;
    if (activeAlertCount > 1) {
        return `${activeAlertCount} Alerts · ${needsAttentionCount} of ${sensorCount} ${units}`;
    }
    if (worstSensor?.attentionMessage)
        return worstSensor.attentionMessage;
    if (needsAttentionCount > 1) {
        return `${needsAttentionCount} of ${sensorCount} ${units} need attention`;
    }
    return activeAlertCount === 1 ? '1 Alert' : undefined;
}
exports.attentionBadgeText = attentionBadgeText;
/**
 * Reorders a category's alerts so the selected sensor's come first, leaving
 * relative order (severity, as `useActiveAlerts` sorts them) intact within
 * each half.
 *
 * Detail screens used to FILTER on the selected sensor, which meant switching
 * systems hid every other system's alerts — a homeowner on the Downstairs tab
 * saw a clean screen while Upstairs had three open faults.
 */
function sortAlertsBySelection(alerts, selectedSensorIds) {
    const selected = new Set(selectedSensorIds.filter(Boolean));
    if (selected.size === 0)
        return alerts;
    const mine = [];
    const others = [];
    alerts.forEach((alert) => {
        if (alert.sensorId && selected.has(alert.sensorId))
            mine.push(alert);
        else
            others.push(alert);
    });
    return [...mine, ...others];
}
exports.sortAlertsBySelection = sortAlertsBySelection;
